/**
 * WSD Session Sorting Utilities
 *
 * Provides stable, deterministic sorting for streaming sessions.
 * Used by both Table and Grouped views for consistent ordering.
 *
 * Sort Contract:
 * 1. PARTITION: Paused sessions (isPaused=true) always render last
 * 2. ACTIVE ORDER: Sort by firstSeenAt DESC (newest join at top)
 * 3. PAUSED ORDER: Same within paused section — firstSeenAt DESC
 * 4. TIE-BREAKERS: userName ASC, then sessionId ASC (for determinism)
 *
 * NOTE: Do NOT use lastActivityAt or playback fields — they change
 * constantly and cause shuffle on every update.
 */

import type { WSDSession } from "../../hooks/useWSDSessions";

/**
 * Extended session type with firstSeenAt for sorting.
 * The firstSeenAt is tracked in the frontend hook, not from the API.
 */
export interface SortableSession extends WSDSession {
  firstSeenAt?: number;
}

/**
 * Comparator function for stable session sorting.
 *
 * Returns negative if `a` should come before `b`.
 *
 * @param a First session
 * @param b Second session
 * @param getFirstSeenAt Function to get firstSeenAt for a session ID
 */
export function compareSessionsStable(
  a: WSDSession,
  b: WSDSession,
  getFirstSeenAt: (id: string) => number
): number {
  // 1. PARTITION: Paused sessions always last
  if (a.isPaused !== b.isPaused) {
    return a.isPaused ? 1 : -1;
  }

  // 2. SORT by firstSeenAt DESC (newest first)
  const aFirstSeen = getFirstSeenAt(a.id);
  const bFirstSeen = getFirstSeenAt(b.id);
  if (aFirstSeen !== bFirstSeen) {
    return bFirstSeen - aFirstSeen; // DESC: higher (newer) timestamp first
  }

  // 3. TIE-BREAKER: userName ASC
  const userCompare = a.userName.localeCompare(b.userName);
  if (userCompare !== 0) {
    return userCompare;
  }

  // 4. TIE-BREAKER: sessionId ASC (final deterministic fallback)
  return a.id.localeCompare(b.id);
}

/**
 * Sort sessions with stable ordering.
 *
 * - Paused sessions always appear last
 * - Newest-joined sessions appear first (within each partition)
 * - Order is deterministic and won't shuffle on data updates
 *
 * @param sessions Array of sessions to sort
 * @param getFirstSeenAt Function to get firstSeenAt timestamp for a session ID
 * @returns New sorted array (does not mutate input)
 */
export function sortSessionsStable(
  sessions: readonly WSDSession[],
  getFirstSeenAt: (id: string) => number
): WSDSession[] {
  return [...sessions].sort((a, b) => compareSessionsStable(a, b, getFirstSeenAt));
}

/**
 * Create a firstSeenAt getter from a Map.
 *
 * Returns the tracked timestamp if available, or the current time as fallback.
 * This ensures new sessions that haven't been tracked yet still sort correctly.
 */
export function createFirstSeenGetter(
  firstSeenMap: Map<string, number>
): (id: string) => number {
  return (id: string): number => {
    return firstSeenMap.get(id) ?? Date.now();
  };
}
